import { useMemo, useState } from "react";
import type { JsonRecord } from "../types";
import { Empty } from "./Feedback";

const hidden = new Set([
  "id", "proyecto_id", "contrato_id", "area_id", "frente_id", "subfrente_id",
  "created_by", "updated_by", "deleted_at", "puede_editar", "puede_eliminar",
]);

function formatHeader(value: string) {
  return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function formatDateValue(value: unknown) {
  if (value == null || value === "") return null;
  const text = String(value);
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:T|$)/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : null;
}

function formatCell(value: unknown) {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Sí" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return formatDateValue(value) || String(value);
}

export function DataTable({
  rows,
  columns,
  actions,
  renderCell,
  tableClassName,
  columnLabels,
  actionsHeader,
  actionsColumnClassName,
  paginate = true,
}: {
  rows: JsonRecord[];
  columns?: string[];
  actions?: (row: JsonRecord) => React.ReactNode;
  renderCell?: (row: JsonRecord, key: string) => React.ReactNode | undefined;
  tableClassName?: string;
  columnLabels?: Record<string, string>;
  actionsHeader?: string | null;
  actionsColumnClassName?: string;
  paginate?: boolean;
}) {
  const [page, setPage] = useState(0);
  const pageSize = 20;
  const visibleColumns = useMemo(() => {
    if (columns) return columns;
    const keys = new Set<string>();
    rows.slice(0, 30).forEach((row) => Object.keys(row).forEach((key) => {
      if (!hidden.has(key)) keys.add(key);
    }));
    return [...keys].slice(0, 9);
  }, [rows, columns]);
  const pages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, pages - 1);
  const pageRows = paginate ? rows.slice(safePage * pageSize, (safePage + 1) * pageSize) : rows;
  const inferredClass = visibleColumns.includes("ubicacion_detalles") ? "compactation-results-table" : "";
  const resolvedTableClassName = [tableClassName, inferredClass].filter(Boolean).join(" ") || undefined;

  if (!rows.length) return <Empty />;
  return <div className="table-wrap">
    <table className={resolvedTableClassName}>
      <thead><tr>
        {visibleColumns.map((key) => <th key={key}>{columnLabels?.[key] || formatHeader(key)}</th>)}
        {actions && <th className={actionsColumnClassName} style={actionsColumnClassName ? undefined : { width: "1%", whiteSpace: "nowrap" }}>{actionsHeader ?? "Acciones"}</th>}
      </tr></thead>
      <tbody>{pageRows.map((row, index) => <tr key={String(row.id || index)}>
        {visibleColumns.map((key) => {
          const custom = renderCell?.(row, key);
          return <td key={key} title={formatCell(row[key])}>{custom === undefined ? formatCell(row[key]) : custom}</td>;
        })}
        {actions && <td
          className={["actions", actionsColumnClassName].filter(Boolean).join(" ")}
          style={{ overflow: "visible", position: "relative", ...(actionsColumnClassName ? {} : { width: "1%", whiteSpace: "nowrap" }) }}
          onClickCapture={(event) => {
            const target = event.target as HTMLElement;
            if (!target.closest("button")) return;
            const menu = target.closest("details") as HTMLDetailsElement | null;
            if (menu) menu.open = false;
          }}
        >{actions(row)}</td>}
      </tr>)}</tbody>
    </table>
    {paginate && pages > 1 && <div className="pagination">
      <span>{rows.length} registros</span>
      <button className="button ghost small" disabled={safePage === 0} onClick={() => setPage((p) => p - 1)}>Anterior</button>
      <span>{safePage + 1} / {pages}</span>
      <button className="button ghost small" disabled={safePage >= pages - 1} onClick={() => setPage((p) => p + 1)}>Siguiente</button>
    </div>}
  </div>;
}
